import React from "react";
import CheckIcon from "@mui/icons-material/Check";
import ClockIcon from "@mui/icons-material/Schedule";
import LoadingIcon from "@mui/icons-material/Autorenew";
import WifiIcon from "@mui/icons-material/Wifi";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import type { GalleryConnection, GoogleImageUploadState } from "./useGoogleImageUpload";
import styles from "./GoogleImageGallery.module.scss";

export function GalleryConnectionBadge({ connection }: { connection: GalleryConnection }) {
  return (
    <span className={`${styles.connection} ${!connection.online || connection.slow ? styles.connectionSlow : ""}`}>
      {connection.online ? <WifiIcon aria-hidden="true" /> : <WifiOffIcon aria-hidden="true" />}
      {!connection.online ? "Offline" : connection.slow ? "Slow connection" : "Online"}
      {connection.online && connection.estimatedMbps && <span>≈ {connection.estimatedMbps} Mbps</span>}
    </span>
  );
}

function formatRate(bytes: number) {
  return bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB/s`
    : `${(bytes / 1024).toFixed(1)} KB/s`;
}

export default function GoogleImageUploadStatus({ state, connection, elapsedSeconds }: {
  state: GoogleImageUploadState;
  connection: GalleryConnection;
  elapsedSeconds: number;
}) {
  if (state.phase !== "sending" && state.phase !== "saving") return null;
  const saving = state.phase === "saving";
  const slow = elapsedSeconds >= 10 || connection.slow;
  const percent = !saving && state.total ? Math.min(100, Math.round(state.loaded / state.total * 100)) : undefined;
  const message = !connection.online
    ? "Your connection was lost. Waiting for a response; check your connection before retrying."
    : slow
      ? "Still working. A slow connection or image source can take longer. You don't need to upload again."
      : saving ? "Your selection has been sent. We're retrieving the image and saving it to your private gallery."
        : "Sending your image selection. We'll show you when it's ready.";

  return (
    <section className={styles.uploadStatus} aria-label="Image upload progress">
      <div className={styles.uploadStatusHeading} role="status" aria-live="polite">
        <LoadingIcon className={styles.spinner} aria-hidden="true" />
        <strong>{saving ? "Saving image to your gallery" : "Sending image selection"}</strong>
        <span aria-live="off">{elapsedSeconds}s elapsed</span>
      </div>
      <div className={styles.progressTrack} role="progressbar" aria-label={saving ? "Saving image" : "Sending image selection"}
        aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}
        aria-valuetext={saving ? "Request sent. Image is still being saved." : percent === undefined ? "Sending" : `${percent}% of request sent`}>
        <span className={percent === undefined ? styles.indeterminate : styles.progressFill} style={percent === undefined ? undefined : { width: `${percent}%` }} />
      </div>
      <p className={styles.statusMessage} aria-live="polite">{message}</p>
      <div className={styles.uploadSteps}>
        <span className={saving ? styles.stepDone : styles.stepActive}>{saving ? <CheckIcon /> : <LoadingIcon className={styles.spinner} />} Send selection</span>
        <span className={saving ? styles.stepActive : ""}>{saving ? <LoadingIcon className={styles.spinner} /> : <ClockIcon />} Save image</span>
        <span><CheckIcon /> Ready in gallery</span>
      </div>
      <dl className={styles.metrics} aria-live="off">
        <div><dt>Request upload speed</dt><dd>{state.bytesPerSecond ? formatRate(state.bytesPerSecond) : saving ? "Request sent" : "Measuring…"}</dd></div>
        <div><dt>Estimated connection</dt><dd>{!connection.online ? "Offline" : connection.estimatedMbps ? `≈ ${connection.estimatedMbps} Mbps` : "Speed unavailable"}</dd></div>
      </dl>
      <p className={styles.speedNote}>Speed is for sending your selection. Image transfer speed from the source is not reported.</p>
    </section>
  );
}
